import {
  createContext, useMemo, useEffect, useState, useCallback,
} from 'react';
import { v4 as uuid } from 'uuid';
import api from '../services/api';
import placeholder from '../assets/img/img_placeholder_20230927.jpg';
import { PaginationMetaLink } from '../models/Paginator';
import { ImageType } from '../utils/image';

export interface ImageItem {
  id: number,
  url: string,
  thumbnail: any,
}

interface ImagesContextProps {
  apiError: string | null,
  validationError: string | null,
  placeHolderImage?: string,
  Create: (data: ArrayBuffer, type: ImageType) => Promise<{ id?: number, url?: string }>,
  GetImages: (type: ImageType, page?: number) => Promise<ImageItem[]>,
  images: ImageItem[]
  total: number
  links?: PaginationMetaLink[]
  currentPage?: number
  toItem?: number
  fromItem?: number
  destroyImage: (id: number, cb?: () => void) => void
  exchangeForUrl: (url: string, defaultUrl?: string) => Promise<string>,
  isLoading: boolean,
}

export const ImagesContext = createContext<ImagesContextProps>({
  Create: (): Promise<{ id: number, url: string }> => Promise.resolve({ id: 0, url: '' }),
  GetImages: (): Promise<{ id: number, url: string, thumbnail: string }[]> => Promise.resolve([]),
  images: [],
  total: 0,
  destroyImage: () => { },
  exchangeForUrl: null,
  apiError: null,
  validationError: null,
  isLoading: true,
});

interface ImagesProviderProps {
  children: React.ReactNode
}

const ImagesProvider: React.FC<ImagesProviderProps> = (props: ImagesProviderProps) => {
  const { children } = props;
  const [placeHolderImage, setPlaceHolderImage] = useState('');
  const [images, setImages] = useState<{ id: number, url: string, thumbnail: string }[]>([]);
  const [total, setTotal] = useState(0);
  const [links, setLinks] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [toItem, setToItem] = useState(0);
  const [fromItem, setFromItem] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [queue, setQueue] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // convert placeholder in static folder into base64 for exporting html
  useEffect(() => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result !== 'string') {
          return;
        }
        setPlaceHolderImage(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', placeholder);
    xhr.responseType = 'blob';
    xhr.send();
  }, []);

  useEffect(() => {
    setIsLoading(queue.length > 0);
  }, [queue]);

  const Create = useCallback(async (data: ArrayBuffer, type: ImageType): Promise<{ id?: number, url?: string }> => {
    setApiError(null);
    setValidationError(null);
    // upload To server
    const blobData = new Blob([data]);
    const formData = new FormData();
    formData.append('file', blobData);
    formData.append('type', type);

    try {
      const res = await api.post('/v1/images/create', formData, { headers: { 'content-type': 'multipart/form-data' } });
      if (res.data == null) {
        return {};
      }
      // update state
      setImages((images) => images.filter((image) => image.id !== res.data.id).concat({
        id: res.data.id,
        url: res.data.url,
        thumbnail: res.data.thumbnail,
      }));
      return { id: res.data.id, url: res.data.url };
    } catch (error: any) {
      if (error.response && error.response.status === 422 && error.response.data.errors) {
        const validationErrors = error.response.data.errors;
        const firstErrorField = Object.keys(validationErrors)[0];
        const firstErrorMessage = validationErrors[firstErrorField][0]; // Get the first error message of the first field

        setValidationError(firstErrorMessage); // <-- Set validation error
      } else {
        setApiError('予期しないエラーが発生しました。');
      }

      return {};
    }
  }, []);

  const GetImages = useCallback(async (type: ImageType, page?: number): Promise<{ id: number, url: string, thumbnail: string }[]> => {
    setApiError(null);

    // Check if the browser perceives it as offline
    if (!navigator.onLine) {
      // "You seem to be offline. Please check your internet connection."
      setApiError('インターネット接続がありません。接続を確認してください。');
      return [];
    }

    const params = page ? { type, page } : { type };

    try {
      const res = await api.get('/v1/images', { params });
      if (res.data == null) {
        return [];
      }
      const result = res.data.data.map((d: { id: number, url: string, thumbnail: string }) => ({
        id: d.id,
        url: d.url,
        thumbnail: d.thumbnail,
      }));
      setImages(result);
      setTotal(res.data.total);
      setLinks(res.data.links);
      setCurrentPage(res.data.current_page);
      setToItem(res.data.to || 0);
      setFromItem(res.data.from || 0);
      return result;
    } catch (error) {
      // Check if error is an instance of Error
      if (error instanceof Error) {
        if (error.message.includes('ERR_INTERNET_DISCONNECTED')) {
          // "You seem to be offline. Please check your internet connection."
          setApiError('インターネット接続がありません。接続を確認してください。');
        } else {
          // "An error occurred while fetching images. Please try again."
          setApiError('画像の取得中にエラーが発生しました。再試行してください。');
        }
      } else {
        // Handle any other type of error (this is unlikely to happen with Axios, but it's good to have just in case)
        setApiError('予期しないエラーが発生しました。'); // "An unexpected error occurred."
      }
      return [];
    }
  }, []);

  const destroyImage = useCallback(async (id: number, cb?: () => void) => {
    await api.delete(`/v1/images/${id}/delete`);
    setImages((list) => list.filter((l) => l.id !== id));
    cb();
  }, []);

  const addToQueue = useCallback((url: string) => {
    setQueue((q) => [...q, url]);
  }, []);

  const removeFromQueue = useCallback((url: string) => {
    setQueue((q) => q.filter((i) => i !== url));
  }, []);

  const exchangeForUrl = useCallback(async (url: string, defaultUrl?: string): Promise<string> => {
    if (!url) {
      return defaultUrl;
    }

    const id = uuid();
    addToQueue(id);

    const result = await api.get('/v1/images/image-stream', { params: { url }, responseType: 'arraybuffer' }).catch((e) => (e.response));
    removeFromQueue(id);

    if (result === undefined) {
      return defaultUrl;
    }

    if (result.status === 404) {
      return '/img/no-image.jpg';
    }
    const blob = new Blob([result.data], { type: 'image/jpeg' });
    return URL.createObjectURL(blob);
  }, [addToQueue, removeFromQueue]);

  const value: ImagesContextProps = useMemo<ImagesContextProps>(() => ({
    apiError,
    validationError,
    placeHolderImage,
    Create,
    GetImages,
    images,
    total,
    links,
    currentPage,
    toItem,
    fromItem,
    destroyImage,
    exchangeForUrl,
    isLoading,
  }), [
    apiError,
    validationError,
    placeHolderImage,
    images,
    Create,
    GetImages,
    links,
    total,
    currentPage,
    toItem,
    fromItem,
    destroyImage,
    exchangeForUrl,
    isLoading,
  ]);

  return (
    <ImagesContext.Provider value={value}>
      {children}
    </ImagesContext.Provider>
  );
};

export default ImagesProvider;
